import React, { useState } from "react";
import { Button } from "./ui/button";
import { ChevronDown, Menu, X } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

const Navigation = () => {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);

  const industries = [
    { id: "logistics", name: "Logistics & Transportation", description: "Fleet optimization, supply chain" },
    { id: "retail", name: "Retail & E-commerce", description: "POS systems, inventory management" },
    { id: "manufacturing", name: "Industrial & Manufacturing", description: "IoT monitoring, automation" },
    { id: "finance", name: "Finance & Fintech", description: "Digital banking, payment systems" },
    { id: "healthcare", name: "Healthcare & Life Sciences", description: "Patient data, compliance systems" },
    { id: "government", name: "Government & Public Sector", description: "Digital transformation, citizen services" },
  ];

  const businessTypes = [
    { id: "startups", name: "Startups", description: "MVP development, rapid scaling" },
    { id: "enterprise", name: "Enterprise", description: "Digital transformation, system integration" },
    { id: "government", name: "Government", description: "Public sector modernization, compliance" },
  ];

  const services = [
    { id: "ai-consulting", name: "AI Consulting", description: "Expert AI solutions" },
    { id: "full-product-development", name: "Full Product Development", description: "Modern responsive websites" },
    { id: "technology-consulting", name: "Technology Consulting", description: "Tech guidance & optimization" },
    { id: "staff-augmentation", name: "Staff Augmentation", description: "Scale your team globally" },
    { id: "data-engineering", name: "Data Engineering", description: "Turn data into advantage" },
    { id: "growth-support", name: "Growth & Capital Support", description: "Funding & market support" },
  ];

  const Dropdown = ({ title, items, linkPrefix }) => (
    <div className="relative group hidden md:block">
      <button
        onClick={() => navigate(linkPrefix)}
        className="flex items-center text-slate-600 hover:text-slate-900 transition-colors"
      >
        {title} <ChevronDown className="w-4 h-4 ml-1" />
      </button>
      <div className="absolute top-full left-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-slate-200 p-4 opacity-0 invisible group-hover:visible group-hover:opacity-100 transition-all z-50">
        <div className="grid grid-cols-1 gap-3">
          {items.map((item) => (
            <Link
              key={item.id}
              to={`${linkPrefix}/${item.id}`}
              className="block p-3 rounded-lg hover:bg-slate-50 transition-colors"
            >
              <div className="font-medium text-slate-900">{item.name}</div>
              <div className="text-sm text-slate-600">{item.description}</div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Navigation */}
      <nav className="hidden md:flex items-center space-x-8">
        <Dropdown title="Services" items={services} linkPrefix="/services" />
        <Dropdown title="Industries" items={industries} linkPrefix="/industries" />
        <Dropdown title="Solutions" items={businessTypes} linkPrefix="/solutions" />

        <Link to="/case-studies" className="text-slate-600 hover:text-slate-900 transition-colors">
          Case Studies
        </Link>
        <a href="/#about" className="text-slate-600 hover:text-slate-900 transition-colors">
          About
        </a>

        <Button
          className="bg-emerald-600 hover:bg-emerald-700"
          onClick={() => window.open("https://calendly.com/admin-tuliolabs/30min", "_blank")}
        >
          Book a Call
        </Button>
      </nav>

      {/* Mobile Navigation */}
      <div className="md:hidden flex items-center">
        <button onClick={() => setMobileOpen(!mobileOpen)}>
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu Dropdown */}
      {mobileOpen && (
        <div className="absolute top-16 left-0 w-full bg-white shadow-lg border-t border-slate-200 z-50 p-4 space-y-3 md:hidden">
          {[
            { title: "Services", items: services, prefix: "/services" },
            { title: "Industries", items: industries, prefix: "/industries" },
            { title: "Solutions", items: businessTypes, prefix: "/solutions" },
          ].map((section) => (
            <div key={section.title}>
              <button
                onClick={() =>
                  setOpenDropdown(openDropdown === section.title ? null : section.title)
                }
                className="w-full flex justify-between items-center text-slate-700 font-medium py-2"
              >
                {section.title}
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${
                    openDropdown === section.title ? "rotate-180" : ""
                  }`}
                />
              </button>

              {openDropdown === section.title && (
                <div className="pl-4 space-y-2">
                  {section.items.map((item) => (
                    <Link
                      key={item.id}
                      to={`${section.prefix}/${item.id}`}
                      onClick={() => setMobileOpen(false)}
                      className="block text-slate-600 hover:text-slate-900 text-sm"
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}

          <Link
            to="/case-studies"
            onClick={() => setMobileOpen(false)}
            className="block text-slate-700 font-medium py-2"
          >
            Case Studies
          </Link>
          <a
            href="/#about"
            onClick={() => setMobileOpen(false)}
            className="block text-slate-700 font-medium py-2"
          >
            About
          </a>

          <Button
            className="w-full bg-emerald-600 hover:bg-emerald-700 mt-2"
            onClick={() =>
              window.open("https://calendly.com/admin-tuliolabs/30min", "_blank")
            }
          >
            Book a Call
          </Button>
        </div>
      )}
    </>
  );
};

export default Navigation;
